import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  BarChart2, 
  ShieldCheck, 
  Diamond, 
  Target, 
  ChevronDown, 
  ChevronUp,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import style from "./TopicMastery.module.css";
import { baseurl } from "../../Url";
import Mastery_Moderate from "../../../assests/Mastery_Moderate.png";
import Mastery_needFocus from "../../../assests/Mastery_needFocus.png";
import Mastery_StrengthIcon from "../../../assests/Mastery_StrengthIcon.png";
import TopicMasteryIcon from "../../../assests/TopicMasteryIcon.png";
import SuccessImage from "../../../assests/ok.png";
import BookImg from "../../../assests/Book.png";
import milestone from "../../../assests/milestone.png";
import RocketImg from "../../../assests/Group 619.png";

const TopicMastery = () => {
  const navigate = useNavigate();
  const [openModule, setOpenModule] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardPages, setCardPages] = useState({});
  const [overallPercentage, setOverallPercentage] = useState(0);
  const [overallProgress, setOverallProgress] = useState(null);
  const [learningData, setLearningData] = useState(null);
  const [hasQuiz, sethasQuiz] = useState(false);

  const fetchDetails = async () => {
    const USERID = localStorage.getItem("USERID");
    const OrgID = localStorage.getItem("ORGID");
    const categoryId = localStorage.getItem("categoryID");
    const token = localStorage.getItem("access-token");

    if (!token) return;

    try {
      const response = await fetch(`${baseurl}/performance/skill_mastery`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: USERID,
          org_id: OrgID,
          category_id: categoryId,
        }),
      });

      const data = await response.json();
      if (response.ok && data?.status_code === 200) {
        setModules(data.data || []);
        setOverallPercentage(data.overall_percentage || 0);
        setOverallProgress(data.overall_progress || { mastered: 0, moderate: 0, needs_focus: 0 });
        sethasQuiz(Boolean(data.has_quiz));
      }

      // Fetch learning details to facilitate navigation to detail page
      const learningResponse = await fetch(`${baseurl}/userlearning/details/v2`, {
        method: "POST",
        body: JSON.stringify({ user_id: USERID }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const learningDataRes = await learningResponse.json();
      if (learningDataRes.status === true && learningDataRes.data?.length > 0) {
        setLearningData(learningDataRes.data[0]);
      }
    } catch (error) {
      console.error("API Error fetching topic mastery or learning details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartLearning = () => {
    navigate("/new/learnings", {
      state: { scrollToModuleSection: true },
    });
  };

  useEffect(() => {
    fetchDetails();
  }, []);

  const toggleModule = (moduleId) => {
    setOpenModule(openModule === moduleId ? null : moduleId);
  };

  const getCleanId = (idObj) => {
    if (!idObj) return "";
    if (typeof idObj === "string") return idObj;
    if (typeof idObj === "object") {
      if (idObj.$oid) return idObj.$oid;
      if (idObj._id) return getCleanId(idObj._id);
    }
    return String(idObj);
  };

  const handleTopicClick = async (perfMod, perfTopic) => {
    let currentLearningData = learningData;

    // Fetch on demand if not loaded yet
    if (!currentLearningData) {
      const USERID = localStorage.getItem("USERID");
      const token = localStorage.getItem("access-token");
      if (!token) return;

      try {
        const learningResponse = await fetch(`${baseurl}/userlearning/details/v2`, {
          method: "POST",
          body: JSON.stringify({ user_id: USERID }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const learningDataRes = await learningResponse.json();
        if (learningDataRes.status === true && learningDataRes.data?.length > 0) {
          currentLearningData = learningDataRes.data[0];
          setLearningData(currentLearningData);
        }
      } catch (e) {
        console.error("Error fetching learning data on click:", e);
      }
    }

    if (!currentLearningData) {
      console.warn("Learning data is not available.");
      return;
    }

    // Find the module in learningData.Topics
    const foundModuleIndex = currentLearningData.Topics?.findIndex((m) => {
      const mId = getCleanId(m.module_id) || getCleanId(m.topic_id);
      const perfModId = getCleanId(perfMod.module_id);
      return mId && perfModId && mId === perfModId;
    });

    if (foundModuleIndex === -1 || foundModuleIndex === undefined) {
      console.warn("Matching module not found in learningData");
      return;
    }

    const foundModule = currentLearningData.Topics[foundModuleIndex];

    const foundTopicIndex = foundModule.topics?.findIndex((t) => {
      const tId = getCleanId(t.id) || getCleanId(t.topic_id);
      const perfTopicId = getCleanId(perfTopic.topic_id);
      return tId && perfTopicId && tId === perfTopicId;
    });

    if (foundTopicIndex === -1 || foundTopicIndex === undefined) {
      console.warn("Matching topic not found in learningData module");
      return;
    }

    const foundTopic = foundModule.topics[foundTopicIndex];
    const USERID = localStorage.getItem("USERID");
    const OrgID = localStorage.getItem("ORGID");

    navigate("/new/learnings/detail", {
      state: {
        mainId: foundModule.topic_id || foundModule.module_id,
        singleTopic: foundTopic,
        learning: currentLearningData.learning_id,
        mapped: currentLearningData.admin_mapped_lessons,
        topic_no: foundTopicIndex + 1,
        userID: USERID,
        orgID: OrgID,
        expandedModuleIndices: [foundModuleIndex],
        scrollToModuleIndex: foundModuleIndex,
      },
    });
  };

  const getProgressColor = (score) => {
    if (score === null || score === undefined || score === 0) return "#D1D5DB"; // Gray / Unattempted
    if (score > 80) return "#00DFC7"; // Turquoise / Strength
    if (score >= 60) return "#FF8800"; // Orange / Moderate
    return "#FF0023"; // Red / Needs Focus
  };

  const getTopicProgressColor = (score) => {
    if (score === null || score === undefined || score === 0) return style.unattemptedBar;
    if (score > 80) return style.strengthBar;
    if (score >= 60) return style.moderateBar;
    return style.needsFocusBar;
  };

  const formatPercentage = (value) => {
    const percentage = Number(value) || 0;
    return Math.min(100, Math.max(0, Math.round(percentage)));
  };

  const hasSessionData = modules.some((mod) =>
    (mod.topics || []).some((topic) => Boolean(topic.attempted))
  );
  const showPercentageHeader = Boolean(hasQuiz) || hasSessionData;

  return (
    <div className={style.outerBorderContainer}>
      <div className={style.topicMasteryContainer}>
        {/* Header Section */}
        <div className={`${style.headerSection} ${showPercentageHeader ? style.hasPercentageHeader : ""}`}>
          <div className={style.headerLeft}>
            <div className={style.headerIconContainer}>
              <img src={TopicMasteryIcon} alt="" />
            </div>
            <div className={style.headerText}>
              <h2 className={style.headerTitle}>Topic Proficiency</h2>
              <p className={style.headerSubtitle}>
                Track your Strengths, improve your weak areas and master every topic
              </p>
            </div>
          </div>
          {showPercentageHeader && (
            <div className={style.percentageBadge}>
              <span className={style.percentageNumber}>{overallPercentage}%</span>
              <span className={style.percentageLabel}>Overall<br />Topic Mastery</span>
            </div>
          )}
        </div>

        {/* Legend section */}
        {showPercentageHeader ? (
          <div className={style.inlineLegendContainer}>
            <div className={style.inlineLegendItem}>
              <span className={`${style.legendDot} ${style.strengthDot}`}></span>
              <span className={style.inlineLegendText}>Strong</span>
            </div>
            <div className={style.inlineLegendItem}>
              <span className={`${style.legendDot} ${style.moderateDot}`}></span>
              <span className={style.inlineLegendText}>Moderate</span>
            </div>
            <div className={style.inlineLegendItem}>
              <span className={`${style.legendDot} ${style.needsFocusDot}`}></span>
              <span className={style.inlineLegendText}>Needs Focus</span>
            </div>
          </div>
        ) : (
          /* Legend Cards */
          <div className={style.legendContainer}>
            <div className={`${style.legendCard} ${style.strengthCard}`}>
              <div className={style.cardHeader}>
                <img src={Mastery_StrengthIcon} alt="" />
                <h4 className={style.strengthTitle}>Strong</h4>
              </div>
              <p className={style.cardText}>
                Mastered topics unlock in green, which means you have a strong understanding
              </p>
            </div>

            <div className={`${style.legendCard} ${style.moderateCard}`}>
              <div className={style.cardHeader}>
                <img src={Mastery_Moderate} alt="" />
                <h4 className={style.moderateTitle}>Moderate</h4>
              </div>
              <p className={style.cardText}>
                You know the topic and you are doing well
              </p>
            </div>

            <div className={`${style.legendCard} ${style.needsFocusCard}`}>
              <div className={style.cardHeader}>
                <img src={Mastery_needFocus} alt="" />
                <h4 className={style.needsFocusTitle}>Needs Focus</h4>
              </div>
              <p className={style.cardText}>
                Constant review and practice is highly recommended
              </p>
            </div>
          </div>
        )}

        {/* Modules Section */}
        <div className={style.modulesSectionHeader}>
          <h3 className={style.modulesTitle}>Modules</h3>
          <p className={style.modulesSubtitle}>
            Overall topic percentage is calculated by taking the average sum of practice (skill) tests score.
          </p>
        </div>

        {/* Accordion Module List */}
        <div className={style.modulesList}>
          {loading ? (
            <div className={style.loadingPlaceholder}>Loading Topic Mastery...</div>
          ) : modules.length === 0 ? (
            <div className={style.loadingPlaceholder}>No module data available.</div>
          ) : (
            modules.map((mod, index) => {
              const isOpen = openModule === mod.module_id;
              const formattedScore = formatPercentage(mod.module_score);

              return (
                <div key={mod.module_id} className={style.moduleAccordionItem}>
                  {/* Accordion Header */}
                  <div 
                    className={`${style.moduleHeaderRow} ${isOpen ? style.openHeader : ""}`}
                    onClick={() => toggleModule(mod.module_id)}
                  >
                    <div className={style.moduleNumberBadge}>
                      {index + 1}
                    </div>

                    <div className={style.moduleDetails}>
                      <h4 className={style.moduleName}>{mod.module_name}</h4>
                      <div className={style.progressBarContainer}>
                        <div className={style.progressTrack}>
                          <div 
                            className={style.progressFill}
                            style={{ 
                              width: `${formattedScore}%`,
                              backgroundColor: getProgressColor(mod.module_score)
                            }}
                          />
                        </div>
                        <span className={style.progressText}>{formattedScore}%</span>
                      </div>
                    </div>

                    <div className={style.toggleIconContainer}>
                      {isOpen ? (
                        <ChevronUp size={22} className={style.chevronIcon} />
                      ) : (
                        <ChevronDown size={22} className={style.chevronIcon} />
                      )}
                    </div>
                  </div>

                  {/* Accordion Content (Topics) */}
                  {isOpen && (
                    <div className={style.topicsExpandArea}>
                      {mod.topics && mod.topics.length > 0 ? (
                        (() => {
                          const strengths = mod.topics
                            .map((t, idx) => ({ ...t, originalIndex: idx + 1 }))
                            .filter(t => {
                              if (!t.attempted) return false;
                              const score = Number(t.topic_score) || 0;
                              return score > 80;
                            });
                          const moderate = mod.topics
                            .map((t, idx) => ({ ...t, originalIndex: idx + 1 }))
                            .filter(t => {
                              if (!t.attempted) return false;
                              const score = Number(t.topic_score) || 0;
                              return score >= 60 && score <= 80;
                            });
                          const needsFocus = mod.topics
                            .map((t, idx) => ({ ...t, originalIndex: idx + 1 }))
                            .filter(t => {
                              if (!t.attempted) return false;
                              const score = Number(t.topic_score) || 0;
                              return score < 60;
                            });

                          const itemsPerPage = 4;

                          // Strengths Pagination
                          const strengthsPages = Math.ceil(strengths.length / itemsPerPage);
                          const strengthsPage = cardPages[`${mod.module_id}_strengths`] || 0;
                          const paginatedStrengths = strengths.slice(
                            strengthsPage * itemsPerPage,
                            (strengthsPage + 1) * itemsPerPage
                          );

                          // Needs Focus Pagination
                          const needsFocusPages = Math.ceil(needsFocus.length / itemsPerPage);
                          const needsFocusPage = cardPages[`${mod.module_id}_needsFocus`] || 0;
                          const paginatedNeedsFocus = needsFocus.slice(
                            needsFocusPage * itemsPerPage,
                            (needsFocusPage + 1) * itemsPerPage
                          );

                          // Moderate Pagination
                          const moderatePages = Math.ceil(moderate.length / itemsPerPage);
                          const moderatePage = cardPages[`${mod.module_id}_moderate`] || 0;
                          const paginatedModerate = moderate.slice(
                            moderatePage * itemsPerPage,
                            (moderatePage + 1) * itemsPerPage
                          );

                          return (
                            <div className={style.cardsContainer}>
                              {/* Strengths Card */}
                              <div className={`${style.masteryCard} ${style.strengthsCard}`}>
                                <div className={style.cardTitleHeader}>
                                  <div className={style.iconBadge}>
                                    <img src={Mastery_StrengthIcon} alt="" />
                                  </div>
                                  <h3>Strong</h3>
                                </div>
                                <div className={style.cardContentArea}>
                                  {paginatedStrengths.length > 0 ? (
                                    paginatedStrengths.map((topic) => (
                                      <div key={topic.topic_id} className={style.cardTopicItem} onClick={() => handleTopicClick(mod, topic)}>
                                        <div className={style.topicItemHeader}>
                                          <span className={style.topicItemLabel}>Topic {topic.originalIndex}</span>
                                          <div className={style.topicItemProgressWrapper}>
                                            <div className={style.topicItemProgressBar}>
                                              <div 
                                                className={`${style.topicItemProgressFill} ${style.strengthsFill}`}
                                                style={{ width: `${formatPercentage(topic.topic_score)}%` }}
                                              />
                                            </div>
                                            <span className={style.topicItemPercentage}>
                                              {formatPercentage(topic.topic_score)}%
                                            </span>
                                          </div>
                                        </div>
                                        <p className={style.topicItemName}>{topic.topic_name}</p>
                                      </div>
                                    ))
                                  ) : (
                                    <div className={style.emptyStateContainer}>
                                      <img src={SuccessImage} alt="No Strengths" className={style.emptyStateImage} />
                                      <p className={style.emptyStateText}>
                                        Keep practicing to build your strengths!
                                      </p>
                                    </div>
                                  )}
                                  {strengthsPages > 1 && (
                                    <div className={style.paginationContainer}>
                                      <button
                                        type="button"
                                        className={style.paginationArrow}
                                        disabled={strengthsPage === 0}
                                        onClick={() => setCardPages(prev => ({
                                          ...prev,
                                          [`${mod.module_id}_strengths`]: Math.max(0, strengthsPage - 1)
                                        }))}
                                      >
                                        <ChevronLeft size={16} />
                                      </button>
                                      <span className={style.paginationText}>
                                        {strengthsPage + 1}/{strengthsPages}
                                      </span>
                                      <button
                                        type="button"
                                        className={style.paginationArrow}
                                        disabled={strengthsPage === strengthsPages - 1}
                                        onClick={() => setCardPages(prev => ({
                                          ...prev,
                                          [`${mod.module_id}_strengths`]: Math.min(strengthsPages - 1, strengthsPage + 1)
                                        }))}
                                      >
                                        <ChevronRight size={16} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Moderate Card */}
                              <div className={`${style.masteryCard} ${style.moderateCard}`}>
                                <div className={style.cardTitleHeader}>
                                  <div className={style.iconBadge}>
                                      <img src={Mastery_Moderate} alt="" />
                                  </div>
                                  <h3>Moderate</h3>
                                </div>
                                <div className={style.cardContentArea}>
                                  {paginatedModerate.length > 0 ? (
                                    paginatedModerate.map((topic) => (
                                      <div key={topic.topic_id} className={style.cardTopicItem} onClick={() => handleTopicClick(mod, topic)}>
                                        <div className={style.topicItemHeader}>
                                          <span className={style.topicItemLabel}>Topic {topic.originalIndex}</span>
                                          <div className={style.topicItemProgressWrapper}>
                                            <div className={style.topicItemProgressBar}>
                                              <div 
                                                className={`${style.topicItemProgressFill} ${style.moderateFill}`}
                                                style={{ width: `${formatPercentage(topic.topic_score)}%` }}
                                              />
                                            </div>
                                            <span className={style.topicItemPercentage}>
                                              {formatPercentage(topic.topic_score)}%
                                            </span>
                                          </div>
                                        </div>
                                        <p className={style.topicItemName}>{topic.topic_name}</p>
                                      </div>
                                    ))
                                  ) : (
                                    <div className={style.emptyStateContainer}>
                                      <img src={SuccessImage} alt="Well Done" className={style.emptyStateImage} />
                                      <p className={style.emptyStateText}>
                                        <strong>Well done!</strong> - You've no topics that need moderate attention.
                                      </p>
                                    </div>
                                  )}
                                  {moderatePages > 1 && (
                                    <div className={style.paginationContainer}>
                                      <button
                                        type="button"
                                        className={style.paginationArrow}
                                        disabled={moderatePage === 0}
                                        onClick={() => setCardPages(prev => ({
                                          ...prev,
                                          [`${mod.module_id}_moderate`]: Math.max(0, moderatePage - 1)
                                        }))}
                                      >
                                        <ChevronLeft size={16} />
                                      </button>
                                      <span className={style.paginationText}>
                                        {moderatePage + 1}/{moderatePages}
                                      </span>
                                      <button
                                        type="button"
                                        className={style.paginationArrow}
                                        disabled={moderatePage === moderatePages - 1}
                                        onClick={() => setCardPages(prev => ({
                                          ...prev,
                                          [`${mod.module_id}_moderate`]: Math.min(moderatePages - 1, moderatePage + 1)
                                        }))}
                                      >
                                        <ChevronRight size={16} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>

                               {/* Needs Focus Card */}
                              <div className={`${style.masteryCard} ${style.needsFocusCard}`}>
                                <div className={style.cardTitleHeader}>
                                  <div className={style.iconBadge}>
                                    <img src={Mastery_needFocus} alt="" />
                                  </div>
                                  <h3>Needs Focus</h3>
                                </div>
                                <div className={style.cardContentArea}>
                                  {paginatedNeedsFocus.length > 0 ? (
                                    paginatedNeedsFocus.map((topic) => (
                                      <div key={topic.topic_id} className={style.cardTopicItem} onClick={() => handleTopicClick(mod, topic)}>
                                        <div className={style.topicItemHeader}>
                                          <span className={style.topicItemLabel}>Topic {topic.originalIndex}</span>
                                          <div className={style.topicItemProgressWrapper}>
                                            <div className={style.topicItemProgressBar}>
                                              <div 
                                                className={`${style.topicItemProgressFill} ${style.needsFocusFill}`}
                                                style={{ width: `${formatPercentage(topic.topic_score)}%` }}
                                              />
                                            </div>
                                            <span className={style.topicItemPercentage}>
                                              {formatPercentage(topic.topic_score)}%
                                            </span>
                                          </div>
                                        </div>
                                        <p className={style.topicItemName}>{topic.topic_name}</p>
                                      </div>
                                    ))
                                  ) : (
                                    <div className={style.emptyStateContainer}>
                                      <img src={SuccessImage} alt="Well Done" className={style.emptyStateImage} />
                                      <p className={style.emptyStateText}>
                                        <strong>Well done!</strong> - You've no topics that need to be focused on.
                                      </p>
                                    </div>
                                  )}
                                  {needsFocusPages > 1 && (
                                    <div className={style.paginationContainer}>
                                      <button
                                        type="button"
                                        className={style.paginationArrow}
                                        disabled={needsFocusPage === 0}
                                        onClick={() => setCardPages(prev => ({
                                          ...prev,
                                          [`${mod.module_id}_needsFocus`]: Math.max(0, needsFocusPage - 1)
                                        }))}
                                      >
                                        <ChevronLeft size={16} />
                                      </button>
                                      <span className={style.paginationText}>
                                        {needsFocusPage + 1}/{needsFocusPages}
                                      </span>
                                      <button
                                        type="button"
                                        className={style.paginationArrow}
                                        disabled={needsFocusPage === needsFocusPages - 1}
                                        onClick={() => setCardPages(prev => ({
                                          ...prev,
                                          [`${mod.module_id}_needsFocus`]: Math.min(needsFocusPages - 1, needsFocusPage + 1)
                                        }))}
                                      >
                                        <ChevronRight size={16} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        <p className={style.noTopicsText}>No topics found for this module.</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Quick Quiz Callout */}
        {!showPercentageHeader && (
          <div className={style.quizCalloutContainer}>
            <div className={style.quizCalloutTextWrapper}>
              <p className={style.quizCalloutDescription}>
                Track your Strengths, improve your weak areas and master every topic to unlock your potential
              </p>
              <h4 className={style.quizCalloutPrompt}>
                Take a quick practice test to see your progress
              </h4>
            </div>
            <button 
              type="button" 
              className={style.quizMeBtn}
              onClick={() => navigate("/practice")}
            >
              Quiz me!
            </button>
          </div>
        )}

        {/* Progress & Next Step Section */}
        {showPercentageHeader && overallProgress && (
          <div className={style.progressSectionGrid}>
            {/* Left Card: Overall Progress */}
            <div className={style.progressAnalysisCard}>
              <div className={style.progressCardHeader}>
                <div className={style.progressIconCircle}>
                  <img src={milestone} alt="Overall Progress" />
                </div>
                <div className={style.progressCardHeaderText}>
                  <h4 className={style.progressCardTitle}>Overall Progress</h4>
                  <p className={style.progressCardSubtitle}>Analyse your score and keep practicing</p>
                </div>
              </div>

              <div className={style.progressCardContent}>
                <div className={style.chartWrapper}>
                  <svg width="140" height="140" viewBox="0 0 140 140" className={style.concentricSvg}>
                    {/* Outer circle: Mastered */}
                    <circle cx="70" cy="70" r="52" fill="transparent" stroke="#00DFC715" strokeWidth="8" />
                    <circle
                      cx="70"
                      cy="70"
                      r="52"
                      fill="transparent"
                      stroke="#00DFC7"
                      strokeWidth="8"
                      strokeDasharray="326.7"
                      strokeDashoffset={326.7 * (1 - (overallProgress.mastered || 0) / 100)}
                      strokeLinecap="round"
                      transform="rotate(-90 70 70)"
                    />
                    {/* Middle circle: Moderate */}
                    <circle cx="70" cy="70" r="40" fill="transparent" stroke="#FFB80015" strokeWidth="8" />
                    <circle
                      cx="70"
                      cy="70"
                      r="40"
                      fill="transparent"
                      stroke="#FFB800"
                      strokeWidth="8"
                      strokeDasharray="251.3"
                      strokeDashoffset={251.3 * (1 - (overallProgress.moderate || 0) / 100)}
                      strokeLinecap="round"
                      transform="rotate(-90 70 70)"
                    />
                    {/* Inner circle: Needs Focus */}
                    <circle cx="70" cy="70" r="28" fill="transparent" stroke="#FF002315" strokeWidth="8" />
                    <circle
                      cx="70"
                      cy="70"
                      r="28"
                      fill="transparent"
                      stroke="#FF0023"
                      strokeWidth="8"
                      strokeDasharray="175.9"
                      strokeDashoffset={175.9 * (1 - (overallProgress.needs_focus || 0) / 100)}
                      strokeLinecap="round"
                      transform="rotate(-90 70 70)"
                    />
                  </svg>
                </div>

                <div className={style.chartLegendList}>
                  <div className={style.chartLegendItem}>
                    <div className={style.legendItemLeft}>
                      <span className={`${style.legendDot} ${style.strengthDot}`} style={{ width: '10px', height: '10px' }}></span>
                      <div className={style.legendItemTextCol}>
                        <span className={style.legendItemName}>Strong</span>
                        <span className={style.legendItemRange}>(80-100%)</span>
                      </div>
                    </div>
                    <span className={style.legendItemPercent}>{overallProgress.mastered || 0}%</span>
                  </div>

                  <div className={style.chartLegendItem}>
                    <div className={style.legendItemLeft}>
                      <span className={`${style.legendDot} ${style.moderateDot}`} style={{ width: '10px', height: '10px' }}></span>
                      <div className={style.legendItemTextCol}>
                        <span className={style.legendItemName}>Moderate</span>
                        <span className={style.legendItemRange}>(60-80%)</span>
                      </div>
                    </div>
                    <span className={style.legendItemPercent}>{overallProgress.moderate || 0}%</span>
                  </div>

                  <div className={style.chartLegendItem}>
                    <div className={style.legendItemLeft}>
                      <span className={`${style.legendDot} ${style.needsFocusDot}`} style={{ width: '10px', height: '10px' }}></span>
                      <div className={style.legendItemTextCol}>
                        <span className={style.legendItemName}>Needs Focus</span>
                        <span className={style.legendItemRange}>(&lt;60%)</span>
                      </div>
                    </div>
                    <span className={style.legendItemPercent}>{overallProgress.needs_focus || 0}%</span>
                  </div>
                </div>
              </div>

              <p className={style.progressFooterNote}>
                These values are shown so that you can analyse and learn better
              </p>
            </div>

            {/* Right Card: Your Next step */}
            <div className={style.nextStepCard}>
              <div className={style.progressCardHeader}>
                <div className={style.rocketIconCircle}>
                 <img src={RocketImg} alt="" />
                </div>
                <div className={style.progressCardHeaderText}>
                  <h4 className={style.progressCardTitle}>Your Next step</h4>
                  <p className={style.progressCardSubtitle}>Analyse your score and keep practicing</p>
                </div>
              </div>

              <div className={style.nextStepActions}>
                <div className={style.nextStepActionRow} onClick={() => navigate("/focus-zone")}>
                  <div className={style.actionRowLeft}>
                    <div className={style.targetIconWrapper}>
                     <img src={Mastery_needFocus} alt="" />
                    </div>
                    <div className={style.actionRowTextCol}>
                      <span className={style.actionRowTitle}>Focus on weak topic</span>
                      <span className={style.actionRowDesc}>Review and strengthen your weak areas.</span>
                    </div>
                  </div>
                  <div className={style.actionRowChevron}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF0023" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </div>
                </div>

                <div className={style.nextStepActionRow} onClick={handleStartLearning}>
                  <div className={style.actionRowLeft}>
                    <div className={style.bookIconWrapper}>
                      <img src={BookImg} alt="" />
                    </div>
                    <div className={style.actionRowTextCol}>
                      <span className={style.actionRowTitle} style={{ color: '#595BDB' }}>Start Learning</span>
                      <span className={style.actionRowDesc}>Continue your learning journey.</span>
                    </div>
                  </div>
                  <div className={style.actionRowChevron}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#595BDB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopicMastery;
